import { useState } from 'react';

import { getRequisitionStatus } from '@helpers/requisition';
import { formatDate } from '@utils/helpers';

import type { HomeOrderEntry } from '../homeRanking';
import { getProductNames } from './HomeOrderCard';

const PRODUCT_SEPARATOR = ', ';

/** Roughly three lines in the Tests included column at desktop width. */
export const TESTS_CHARACTER_BUDGET = 80;

/**
 * Trims to whole product names rather than mid-word, and always keeps the first
 * one however long it is.
 */
export const splitProductNamesAtBudget = (names: string[], budget: number) => {
  let length = 0;
  let shownCount = 0;

  for (const name of names) {
    const nextLength = shownCount ? length + PRODUCT_SEPARATOR.length + name.length : name.length;
    if (shownCount && nextLength > budget) break;
    length = nextLength;
    shownCount += 1;
  }

  return { shown: names.slice(0, shownCount), hiddenCount: names.length - shownCount };
};

function TestsIncluded({ names }: { names: string[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { shown, hiddenCount } = splitProductNamesAtBudget(names, TESTS_CHARACTER_BUDGET);

  if (!hiddenCount) return <>{names.join(PRODUCT_SEPARATOR)}</>;

  return (
    <>
      {(isExpanded ? names : shown).join(PRODUCT_SEPARATOR)}
      {!isExpanded && <span aria-hidden='true'>&hellip;</span>}{' '}
      <button
        type='button'
        className='home-order-table__toggle'
        onClick={() => setIsExpanded(expanded => !expanded)}
        aria-expanded={isExpanded}
        data-testid='homeOrderRowTestsToggle'
      >
        {isExpanded ? 'Show less' : `Show ${hiddenCount} more`}
      </button>
    </>
  );
}

export interface HomeOrderRowProps {
  entry: HomeOrderEntry;
}

export const HomeOrderRow = (props: HomeOrderRowProps) => {
  const { entry } = props;
  const { requisition } = entry;
  const status = getRequisitionStatus(requisition);

  return (
    <li className='home-order-table__row' data-testid='homeOrderRow'>
      <span className='home-order-table__cell home-order-table__cell--id'>{requisition.requisition_id}</span>
      <span className='home-order-table__cell home-order-table__cell--date'>
        {requisition.submitted_at ? formatDate(requisition.submitted_at) : null}
      </span>
      <span className='home-order-table__cell'>
        <TestsIncluded names={getProductNames(requisition)} />
      </span>
      <span className='home-order-table__cell home-order-table__cell--status'>
        {status && (
          <span className='home-status-badge' data-testid='homeOrderRowStatus'>
            {status}
          </span>
        )}
      </span>
    </li>
  );
};
